import { StrictMode } from 'react';
import { createRoot, hydrateRoot } from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import type { InitialState } from '@astrologermatch/shared';
import { App } from './App.js';
import { HeadProvider } from './lib/seo.js';
import { fetchPageData } from './lib/state.js';
import './styles.css';

function readInitialState(): InitialState | null {
  const el = document.getElementById('__INITIAL_STATE__');
  if (!el?.textContent) return null;
  return JSON.parse(el.textContent) as InitialState;
}

function tree(initial: InitialState) {
  return (
    <StrictMode>
      <HeadProvider collector={null}>
        <BrowserRouter>
          <App initial={initial} />
        </BrowserRouter>
      </HeadProvider>
    </StrictMode>
  );
}

async function start() {
  const container = document.getElementById('root');
  if (!container) throw new Error('#root element not found');

  const embedded = readInitialState();
  if (embedded) {
    // Server-rendered page: attach to the existing markup.
    hydrateRoot(container, tree(embedded));
    return;
  }
  // `vite` dev server (no SSR): ask the API for the same data the server would have embedded.
  const path = window.location.pathname;
  const data = await fetchPageData(path);
  if (data.redirect) {
    window.location.replace(data.redirect);
    return;
  }
  const initial: InitialState = {
    url: path + window.location.search,
    settings: data.settings,
    page: data.page,
  };
  createRoot(container).render(tree(initial));
}

void start();
